import { apiErrorField } from '@agos/api-client'
import {
  getGetEmployeesQueryKey,
  useDeleteEmployeesId,
  useGetEmployees,
  usePatchEmployeesId,
  usePostEmployees,
} from '@agos/api-client/hr'
import { useGetHikvisionConfig } from '@agos/api-client/devices'
import {
  T,
  alsoDrivesOf,
  formatCount,
  roleBadgeColor,
  roleDisplayName,
  tt,
  useAuth,
  useFranchiseModules,
  useModalFocus,
} from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  cn,
  type Column,
  ConfirmDialog,
  DataTable,
  FilterBar,
  FilterSelect,
  IconButton,
  PageHeader,
  PhoneInput,
  ReadFailedBlock,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
  useToast,
} from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  Mail,
  Phone,
  Plus,
  Trash2,
  UserCheck2,
  UserX,
  Users,
  X,
} from 'lucide-react'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { Link } from 'wouter'

import { roleRefusalCode, useAssignRole, useRolesOverview, type RoleView } from '../roles/roles-api'
import {
  SYSTEM_ROLES,
  canSaveEmployee,
  chooseRole,
  customRoleChoices,
  customRolesOf,
  deactivateRestriction,
  deleteRestriction,
  demotionLocksEveryoneOut,
  driftedPositions,
  emptyEmployeeForm,
  employeeFormOf,
  employeeProblems,
  employeeSave,
  filterRoster,
  initialsOf,
  mayManageStaff,
  mayRemoveStaff,
  mayOpenRolesScreen,
  positionSuggestions,
  positionsThatAreRoles,
  restrictionText,
  roleAccess,
  roleChangeSignsOut,
  roleColor,
  roleLabel,
  roleNote,
  roleOptions,
  rolesOfferedTo,
  staffEditLock,
  staffLockText,
  type StaffReader,
  rosterRoleKey,
  rosterRoleOf,
  rosterTotals,
  signOutWarning,
  type Employee,
  type EmployeeForm,
  type EmployeeProblem,
  type Restriction,
  type RosterRole,
} from './staff-roster'

/**
 * `/employees` — who works here and what each of them can reach.
 *
 * **FB-099.** Legacy's role filter lists four of the five roles: no `cook`. There is one cook on the
 * staff, so that person cannot be filtered for and the chip counts do not add up — *Všetky* says 23
 * against 22 across the four. And `cook` is the form's **default**, so an employee added without
 * touching the dropdown lands on the role the filter cannot select. The options are built from the
 * roles the staff actually hold, and the default is the commonest non-administrator one.
 *
 * **FB-100.** `position` is free text and the data shows it: fourteen spellings for twelve jobs,
 * `Šofer` six times beside `Šofér` twice, `veduci` beside `Veduci`, and one employee whose position is
 * the literal string `waiter`. The field offers what is already in use, warns when a new spelling
 * means an existing one, and refuses a system role typed into it.
 *
 * Ported from legacy `pages/employees.tsx` (590 lines).
 */

interface HikvisionConfig {
  ip?: string | null
}

/** The dialog's state: the form, whose record it edits, and the role that record held when it was read. */
interface Editing {
  id: number | null
  form: EmployeeForm
  /** `COALESCE(role_key, system_role)` as read - what a move is checked against (`expectedRoleKey`); null for new. */
  heldRoleKey: string | null
}

export function EmployeesPage() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user, managesRoles, hasPermission } = useAuth()
  const { isModuleEnabled } = useFranchiseModules()

  const { data, isLoading, isError, refetch } = useGetEmployees()
  const employees = useMemo<Employee[]>(() => (data ?? []), [data])
  // The card field only makes sense once a terminal exists — legacy's own rule, `!!cfg?.ip`.
  const { data: terminalData } = useGetHikvisionConfig()
  const terminal = (terminalData ?? {}) as HikvisionConfig
  const terminalConfigured = (terminal.ip ?? '') !== ''

  const [tab, setTab] = useState<'active' | 'inactive'>('active')
  const [role, setRole] = useState('all')
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState<Editing | null>(null)
  const [toggling, setToggling] = useState<Employee | null>(null)
  const [deleting, setDeleting] = useState<Employee | null>(null)
  const [saving, setSaving] = useState(false)

  /* §9.758, on BACKEND.md §10A.413 (owner: *"administrator cant create new employee"*) - the server's employee writes
     ask `employees_edit` and `employees_delete` now, so the controls follow the rights; a holder of them manages cooks,
     waiters and drivers, never an administrator's row or their own (`staffEditLock`). */
  const reader = useMemo<StaffReader>(
    () => ({ systemRole: user?.systemRole, id: user?.id, hasPermission }),
    [user, hasPermission],
  )
  const mayManage = mayManageStaff(reader)
  const mayRemove = mayRemoveStaff(reader)

  /* O119, FRONTEND.md §9.753 - the custom roles, read for whoever the overview answers: a main administrator, who
     offers them in the role select, and somebody whose role manages another, who sees the ones they manage by name.
     Anybody else is refused it (`ROLE_NOT_MANAGED`), so nothing is asked and the roster names a custom role by its
     number. **And only while the package has the `permissions` module**: the overview is `@RequireModule`'d
     (§10A.408), so a franchise sold the `basic` tier would otherwise be told on every open of the dialog that its
     custom roles could not be read, about a read its package refused. */
  const rolesModule = isModuleEnabled('permissions')
  const rolesRead = useRolesOverview({ query: { enabled: (mayManage || managesRoles) && rolesModule } })
  /* §9.758 - somebody who manages staff and no role is refused the overview (`ROLE_NOT_MANAGED`): there are simply
     no custom roles to offer them, which is not a read that failed. */
  const rolesUnread = rolesRead.isError && roleRefusalCode(rolesRead.error) !== 'ROLE_NOT_MANAGED'
  const roles = rolesRead.data?.roles
  const customRoles = useMemo(() => customRolesOf(roles), [roles])
  // The legend's way to *Role a práva*, for whoever the route lets in - a link to a refusal is §9.7's defect.
  const mayOpenRoles = mayOpenRolesScreen({
    systemRole: user?.systemRole,
    managesRoles,
    permissionsModule: rolesModule,
  })

  const totals = rosterTotals(employees)
  const options = roleOptions(employees, tab, roles)
  const shown = filterRoster(employees, { tab, role, search })
  const drifted = driftedPositions(employees)
  const roleTyped = positionsThatAreRoles(employees)

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesQueryKey() })
  }
  const onError = (error: unknown) =>
    toast({
      title: tt('Chyba'),
      description: messageOf(error) ?? tt('Zmena sa neuložila.'),
      variant: 'destructive',
    })

  /* The dialog's three writes have no callbacks of their own: a save is one to three requests (`employeeSave`), so
     `save` below closes the dialog once, after the last, and says which half took when one is refused. */
  const create = usePostEmployees()
  const update = usePatchEmployeesId()
  const assign = useAssignRole()
  const patch = usePatchEmployeesId({
    mutation: {
      onSuccess: () => {
        setToggling(null)
        invalidate()
      },
      onError,
    },
  })

  /**
   * Saves the dialog - O119, FRONTEND.md §9.753. A move that involves a custom role is the assignment
   * (`PUT /employees/{id}/role`), first, so a refused move leaves everything as it was; the other fields follow through
   * `PATCH` - unless the move ends the editor's own session, when it goes last (`employeeSave`'s `assignLast`). A move
   * between two built-in roles is the `PATCH` alone, as it always was. A new person in a custom role is a `POST` with the
   * base, then the assignment - and should that be refused, the person exists with the base, which is said rather than
   * retried, because a second press would create them twice.
   *
   * **A refusal says which half took**: a move that went through before a refused `PATCH` (or a `PATCH` before a
   * refused move) is said in those words, not as *Zmena sa neuložila*; and a move refused because somebody else moved
   * the person first (`ROLE_STALE`) puts the role they hold now into the dialog, so the next press does not send the
   * same refused request again.
   */
  const save = async (draft: Editing): Promise<void> => {
    const record = employees.find((employee) => employee.id === draft.id)
    const endsOwnSession = draft.id !== null && draft.id === user?.id && roleChangeSignsOut(draft.form, record)
    const plan = employeeSave(
      draft.id,
      draft.form,
      draft.heldRoleKey,
      endsOwnSession,
      user?.systemRole !== 'main_admin',
    )
    setSaving(true)
    let moved = false
    let patched = false
    try {
      if (plan.kind === 'create') {
        const created = await create.mutateAsync({ data: plan.payload })
        invalidate()
        if (plan.assign !== null) {
          try {
            await assign.mutateAsync({ id: created.id, data: plan.assign })
          } catch (refused) {
            setEditing(null)
            toast({
              title: tt('Zamestnanec pridaný bez vlastnej roly'),
              description: `${messageOf(refused) ?? tt('Rolu sa nepodarilo prideliť.')} ${tt(
                'Pracuje zatiaľ ako vstavaná rola - zmeníte ju cez Upraviť.',
              )}`,
              variant: 'destructive',
            })
            return
          }
        }
        setEditing(null)
        toast({ title: tt('Zamestnanec pridaný') })
        return
      }
      if (plan.assign !== null && !plan.assignLast) {
        await assign.mutateAsync({ id: plan.id, data: plan.assign })
        moved = true
        // The move has been made: should the `PATCH` below be refused, a second press must not ask for it again.
        const movedTo = plan.assign.roleKey
        setEditing((current) =>
          current !== null && current.id === plan.id ? { ...current, heldRoleKey: movedTo } : current,
        )
      }
      await update.mutateAsync({ id: plan.id, data: plan.payload })
      patched = true
      // The editor's own change of base, after everything else is saved: it signs them out.
      if (plan.assign !== null && plan.assignLast) {
        await assign.mutateAsync({ id: plan.id, data: plan.assign })
      }
      setEditing(null)
      invalidate()
    } catch (error) {
      if (plan.kind === 'update' && roleRefusalCode(error) === 'ROLE_STALE') {
        await takeTheRoleHeldNow(plan.id, error, patched)
        return
      }
      // The roster is read again either way, so what the list shows is what the server holds after a half-save.
      invalidate()
      if (moved) {
        toast({
          title: tt('Rola sa zmenila, ostatné údaje sa neuložili'),
          description: messageOf(error) ?? tt('Zmena sa neuložila.'),
          variant: 'destructive',
        })
      } else if (patched) {
        toast({
          title: tt('Údaje sa uložili, rola sa nezmenila'),
          description: messageOf(error) ?? tt('Rolu sa nepodarilo zmeniť.'),
          variant: 'destructive',
        })
      } else {
        onError(error)
      }
    } finally {
      setSaving(false)
    }
  }
  /**
   * Somebody else moved this person since the dialog read them, and the move was refused (`ROLE_STALE`, §10A.408). The
   * roster is read again and the dialog takes the role they hold now - kept the typed fields, with the role select back
   * on what the server holds - so the next press saves the rest rather than sending the refused move again.
   */
  const takeTheRoleHeldNow = async (id: number, refusal: unknown, restSaved: boolean): Promise<void> => {
    const fresh = await refetch()
    const row = (fresh.data ?? []).find((employee) => employee.id === id)
    if (row !== undefined) {
      const held = employeeFormOf(row)
      setEditing((current) =>
        current !== null && current.id === id
          ? {
              ...current,
              heldRoleKey: rosterRoleKey(row),
              form: { ...current.form, roleKey: held.roleKey, systemRole: held.systemRole },
            }
          : current,
      )
    }
    toast({
      title: restSaved
        ? tt('Údaje sa uložili, rolu medzitým zmenil niekto iný')
        : tt('Rolu medzitým zmenil niekto iný'),
      description: `${messageOf(refusal) ?? tt('Rola sa nezmenila.')} ${tt(
        'V okne je teraz rola, ktorú má - ak ju chcete zmeniť, vyberte ju znova.',
      )}`,
      variant: 'destructive',
    })
  }

  const remove = useDeleteEmployeesId({
    mutation: {
      onSuccess: () => {
        setDeleting(null)
        invalidate()
      },
      onError,
    },
  })

  // A role the filter no longer offers would otherwise show an empty list with no explanation.
  const setTabAndKeepRole = (next: 'active' | 'inactive') => {
    setTab(next)
    const stillThere = roleOptions(employees, next, roles).some((option) => option.value === role)
    if (!stillThere) setRole('all')
  }

  const columns: Column<Employee>[] = useMemo(
    () => [
      {
        key: 'name',
        header: tt('Meno'),
        sortable: true,
        sortValue: (employee) => employee.name ?? '',
        render: (employee) => (
          <div className="flex items-center gap-3 min-w-0">
            <span className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold shrink-0">
              {initialsOf(employee.name)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate flex items-center gap-1.5">
                {employee.name}
                {employee.id === user?.id && (
                  <span
                    className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full"
                    data-self
                  >
                    <T>Ja</T>
                  </span>
                )}
              </p>
              <p className="text-[11px] text-muted-foreground truncate">{employee.position}</p>
            </div>
          </div>
        ),
      },
      {
        key: 'role',
        header: tt('Rola'),
        sortable: true,
        sortValue: (employee) => rosterRoleOf(employee, roles).label,
        render: (employee) => (
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <RoleBadge role={rosterRoleOf(employee, roles)} />
            {/* §9.685, owner #25 - who else drives, without opening every account to find out. */}
            {employee.systemRole !== 'driver' && alsoDrivesOf(employee) && (
              <span className="text-[10px] font-semibold text-muted-foreground" data-employee-also-drives-mark>
                <T>+ rozvoz</T>
              </span>
            )}
          </span>
        ),
      },
      {
        key: 'email',
        header: tt('E-mail'),
        sortable: true,
        sortValue: (employee) => employee.email ?? '',
        render: (employee) =>
          employee.email ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
              <Mail className="w-3 h-3 shrink-0" />
              <span className="truncate">{employee.email}</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
      },
      {
        key: 'phone',
        header: tt('Telefón'),
        render: (employee) =>
          employee.phone ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <Phone className="w-3 h-3 shrink-0" />
              {employee.phone}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">-</span>
          ),
      },
      {
        key: 'actions',
        header: '',
        render: (employee) => {
          if (!mayManage && !mayRemove) return null
          // §9.758 - an administrator's row, a main administrator's and the reader's own offer no edit, with why.
          const lock = staffEditLock(reader, employee)
          const lockText = lock === null ? undefined : staffLockText(lock)
          const toggleRestriction = deactivateRestriction(employee, employees)
          const deleteRestricted = deleteRestriction(employee, employees, user?.id)
          const toggleHint = toggleRestriction === null ? lockText : restrictionText(toggleRestriction)
          const deleteHint = deleteRestricted === null ? lockText : restrictionText(deleteRestricted)
          return (
            <div className="flex items-center gap-1 justify-end" data-staff-lock={lock ?? undefined}>
              {/* Legacy hides the toggle on a main admin and explains only after the click. */}
              {mayRemove && (
                <IconButton
                  label={`${employee.active === false ? tt('Aktivovať') : tt('Deaktivovať')} ${employee.name ?? ''}`}
                  disabled={toggleRestriction !== null || lock !== null}
                  hint={toggleHint}
                  onClick={() => setToggling(employee)}
                >
                  {employee.active === false ? (
                    <UserCheck2 className="w-3.5 h-3.5" />
                  ) : (
                    <UserX className="w-3.5 h-3.5" />
                  )}
                </IconButton>
              )}
              {mayManage && (
                <button
                  type="button"
                  disabled={lock !== null}
                  title={lockText}
                  data-employee-edit
                  onClick={() =>
                    setEditing({
                      id: employee.id ?? null,
                      form: employeeFormOf(employee),
                      heldRoleKey: rosterRoleKey(employee),
                    })
                  }
                  aria-label={`${tt('Upraviť')} ${employee.name ?? ''}`}
                  className="px-2 py-1.5 rounded-lg border border-border text-xs font-semibold hover:bg-muted/50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <T>Upraviť</T>
                </button>
              )}
              {mayRemove && (
                <IconButton
                  label={`${tt('Zmazať')} ${employee.name ?? ''}`}
                  tone="red"
                  disabled={deleteRestricted !== null || lock !== null}
                  hint={deleteHint}
                  onClick={() => setDeleting(employee)}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </IconButton>
              )}
            </div>
          )
        },
      },
    ],
    [employees, user, reader, mayManage, mayRemove, roles],
  )

  /* §9.543 - *Pridať zamestnanca* is no write over a failed read: the dialog's only duplicate-e-mail guard compares
     against the roster, and over a roster that never arrived it compares against nobody (the server checks no
     uniqueness, and sign-in reads the first row with that address). Disabled with its reason rather than hidden, so a
     main administrator does not read the failure as a missing right. */
  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Zamestnanci')}
        description={
          // A failed read is not a franchise with nobody on the staff (§9.529), so it gives no figure at all.
          isError
            ? undefined
            : `${formatCount(totals.active)} ${tt('aktívnych')} · ${formatCount(totals.inactive)} ${tt('neaktívnych')}`
        }
        icon={<Users className="w-5 h-5" />}
        actions={
          mayManage ? (
            <button
              type="button"
              disabled={isError}
              title={isError ? tt('Najprv sa musí načítať zoznam zamestnancov.') : undefined}
              data-employee-add
              onClick={() =>
                setEditing({ id: null, form: emptyEmployeeForm(employees), heldRoleKey: null })
              }
              className={cn('flex items-center gap-2 px-4 py-2 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              <Plus className="w-4 h-4" />
              <T>Pridať zamestnanca</T>
            </button>
          ) : undefined
        }
      />

      {!mayManage && (
        <p className="text-sm text-muted-foreground" data-staff-read-only>
          <T>Zamestnancov pridáva a upravuje hlavný administrátor alebo ten, kto má právo Zamestnanci - Upraviť.</T>
        </p>
      )}

      {/* O119 - the roster's own read, which this screen never asked about: a failure drew *0 aktívnych* and *Nikto
          nezodpovedá filtru*, a franchise with nobody on the staff. It replaces the figures and the list, which all
          come off it; the legend below does not. */}
      {isError ? (
        <ReadFailedBlock
          marker="read"
          of="employees"
          title={tt('Zamestnancov sa nepodarilo načítať')}
          onRetry={() => void refetch()}
        >
          <T>Server zoznam zamestnancov nevrátil, preto tu nie sú počty ani zoznam - nie je to prázdna franšíza.</T>
        </ReadFailedBlock>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-roster-totals>
            <StatCard label={tt('Zamestnancov')} value={formatCount(totals.total)} icon={Users} color="blue" />
            <StatCard label={tt('Aktívnych')} value={formatCount(totals.active)} icon={UserCheck2} color="emerald" />
            <StatCard
              label={tt('Neaktívnych')}
              value={formatCount(totals.inactive)}
              icon={UserX}
              color={totals.inactive > 0 ? 'amber' : 'emerald'}
            />
            <StatCard
              label={tt('Rôznych pozícií')}
              value={formatCount(positionSuggestions(employees).length)}
              icon={AlertCircle}
              color={drifted.length > 0 ? 'amber' : 'emerald'}
              sub={drifted.length > 0 ? `${drifted.length} ${tt('napísaných dvojako')}` : undefined}
            />
          </div>

          {/* FB-100 - the drift, named. */}
          {drifted.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-position-drift>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <span>
                <T>Rovnaká pozícia je napísaná dvojako:</T>{' '}
                {drifted.map((group) => group.spellings.join(' / ')).join(' · ')}.{' '}
                <T>Každý prehľad podľa pozície ich počíta ako dve rôzne.</T>
              </span>
            </p>
          )}
          {roleTyped.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-position-is-role>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <span>
                <T>V poli pozícia je systémová rola:</T> {roleTyped.join(', ')}.{' '}
                <T>Pozícia je to, čo človek robí; rola je to, čo vidí v systéme.</T>
              </span>
            </p>
          )}

          <TabGroup
            variant="bar"
            tabs={[
              { id: 'active', label: `${tt('Aktívni')} (${totals.active})` },
              { id: 'inactive', label: `${tt('Neaktívni')} (${totals.inactive})` },
            ]}
            active={tab}
            onChange={(id) => setTabAndKeepRole(id as 'active' | 'inactive')}
          />

          <FilterBar
            search={{
              value: search,
              onChange: setSearch,
              placeholder: tt('Hľadať meno, e-mail, telefón alebo pozíciu...'),
            }}
            filters={
              <FilterSelect
                value={role}
                label={tt('Rola')}
                onChange={setRole}
                options={options.map((option) => ({
                  value: option.value,
                  label: `${option.label} (${option.count})`,
                }))}
              />
            }
          />

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <div data-employees>
              <DataTable<Employee>
                columns={columns}
                data={shown}
                rowKey={(employee) => employee.id ?? 0}
                pageSize={25}
                compact
                emptyMessage={tt('Nikto nezodpovedá filtru.')}
                emptyIcon={<Users className="w-10 h-10 mb-3 opacity-30" />}
              />
            </div>
          )}
        </>
      )}

      {/* Legacy's legend, kept - it is the only place the roles' reach is written down. O119 (§9.753): the custom
          roles follow the five with what they work as and the description their main administrator wrote. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-role-legend>
        {SYSTEM_ROLES.map((systemRole) => (
          <div
            key={systemRole}
            className="rounded-2xl border border-border bg-card p-4 space-y-1.5"
            data-role-legend-built-in={systemRole}
          >
            <StatusBadge label={roleLabel(systemRole)} color={roleColor(systemRole)} />
            {roleAccess(systemRole).map((entry) => (
              <p key={entry} className="text-[11px] text-muted-foreground">
                · {entry}
              </p>
            ))}
          </div>
        ))}
        {customRoles.map((custom) => (
          <CustomRoleLegend key={custom.key} role={custom} />
        ))}
      </div>
      {mayOpenRoles && (
        <p className="text-xs text-muted-foreground" data-role-legend-link>
          <T>Čo ktorá rola smie, vlastné roly a kto ich spravuje sa nastavuje v</T>{' '}
          <Link href="/permissions" className="font-semibold text-primary hover:underline">
            <T>Role a práva</T>
          </Link>
          .
        </p>
      )}

      {editing !== null && (
        <EmployeeDialog
          form={editing.form}
          editingId={editing.id}
          employees={employees}
          terminalConfigured={terminalConfigured}
          saving={saving}
          roles={roles}
          rolesUnread={rolesUnread}
          onChange={(next) => setEditing({ ...editing, form: next })}
          onClose={() => setEditing(null)}
          reader={reader}
          onSave={() => void save(editing)}
        />
      )}

      <ConfirmDialog
        open={toggling !== null}
        onClose={() => setToggling(null)}
        onConfirm={() =>
          toggling?.id !== undefined &&
          patch.mutate({ id: toggling.id, data: { active: toggling.active === false } })
        }
        title={
          toggling?.active === false
            ? `${tt('Aktivovať')} ${toggling?.name ?? ''}?`
            : `${tt('Deaktivovať')} ${toggling?.name ?? ''}?`
        }
        description={
          toggling?.active === false
            ? tt('Zamestnanec sa bude môcť znova prihlásiť.')
            : tt('Zamestnanec sa nebude môcť prihlásiť. Jeho história objednávok zostáva.')
        }
        confirmLabel={toggling?.active === false ? tt('Aktivovať') : tt('Deaktivovať')}
        loading={patch.isPending}
      />

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting?.id !== undefined && remove.mutate({ id: deleting.id })}
        title={`${tt('Zmazať')} ${deleting?.name ?? ''}?`}
        description={tt(
          'Zamestnanca to odstráni zo zoznamu. Deaktivácia je zvyčajne to, čo chcete - tá mu zoberie prístup a históriu ponechá.',
        )}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      />
    </div>
  )
}

// ─── the form ────────────────────────────────────────────────────────────────

function EmployeeDialog({
  form,
  editingId,
  employees,
  terminalConfigured,
  saving,
  roles,
  rolesUnread,
  onChange,
  onClose,
  onSave,
  reader,
}: {
  form: EmployeeForm
  editingId: number | null
  employees: Employee[]
  terminalConfigured: boolean
  saving: boolean
  /** O119, §9.753 - the overview's roles, for the custom ones the select offers; absent while unread. */
  roles: readonly RoleView[] | undefined
  /** The overview did not answer, so only the built-ins are offered - and the dialog says so. */
  rolesUnread: boolean
  onChange: (next: EmployeeForm) => void
  onClose: () => void
  onSave: () => void
  /** §9.742, §9.758 - who is editing: a main administrator is offered all five roles, anybody else the line roles. */
  reader: StaffReader
}) {
  const problems = employeeProblems(form, employees, editingId)
  const existing = employees.find((employee) => employee.id === editingId)
  const locksOut =
    existing === undefined ? false : demotionLocksEveryoneOut(form, existing, employees)
  const suggestions = positionSuggestions(employees)
  /* O119, §9.753 - the custom roles under their own group, the record's own kept when it cannot be read, and the
     sentence a change of base needs before the save: the token carries the base, so changing it signs the person
     out on every device (BACKEND.md §10A.406). */
  const customChoices = customRoleChoices(
    roles,
    existing === undefined ? null : { roleKey: rosterRoleKey(existing), systemRole: existing.systemRole },
    reader,
  )
  const signsOut = roleChangeSignsOut(form, existing)

  /* §9.495, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing. Wrapper is the dismiss scrim, so
     the role stays on it and the trap is bounded by the card (§9.469). O119 (§9.753) made the save one to three
     requests, so the close is refused while they run (§9.491's rule for a dialog with a pending flag to hand): a
     refusal of the second would otherwise be reported over a closed dialog with the typed fields gone. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, saving ? null : onClose)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={editingId === null ? tt('Nový zamestnanec') : tt('Upraviť zamestnanca')}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        ref={panelRef}
        onKeyDown={onKeyDown}
        data-employee-dialog
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h3 className="font-bold text-sm">
            {editingId === null ? tt('Nový zamestnanec') : `${tt('Upraviť')} ${form.name}`}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label={tt('Zatvoriť')}
            data-dialog-cancel
            className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-50"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          <Field label={tt('Meno')}>
            <input
              value={form.name}
              onChange={(event) => onChange({ ...form, name: event.target.value })}
              aria-label={tt('Meno')}
              className={INPUT}
            />
          </Field>

          <Field label={tt('Pozícia')}>
            <input
              value={form.position}
              onChange={(event) => onChange({ ...form, position: event.target.value })}
              aria-label={tt('Pozícia')}
              list="agos-positions"
              className={INPUT}
            />
            {/* FB-100 - the spellings already in use, so the next one matches. */}
            <datalist id="agos-positions" data-position-suggestions>
              {suggestions.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
          </Field>

          <Field label={tt('Rola v systéme')}>
            <select
              value={form.roleKey}
              onChange={(event) => onChange(chooseRole(form, event.target.value, customChoices))}
              aria-label={tt('Rola v systéme')}
              className={INPUT}
            >
              <optgroup label={tt('Vstavané')}>
                {rolesOfferedTo(reader, form.systemRole).map((systemRole) => (
                  <option key={systemRole} value={systemRole}>
                    {roleLabel(systemRole)}
                  </option>
                ))}
              </optgroup>
              {customChoices.length > 0 && (
                <optgroup label={tt('Vlastné roly')} data-employee-custom-roles>
                  {customChoices.map((choice) => (
                    <option key={choice.key} value={choice.key}>
                      {choice.label}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="text-[11px] text-muted-foreground">{roleNote(form, roles)}</p>
            {rolesUnread && (
              <p className="text-[11px] text-amber-500" data-employee-roles-unread>
                <T>Vlastné roly sa nepodarilo načítať - ponúkajú sa len vstavané.</T>
              </p>
            )}
            {signsOut && (
              <p className="flex items-start gap-1.5 text-xs text-amber-500" data-role-change-signs-out>
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {signOutWarning(existing?.name)}
              </p>
            )}
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={tt('E-mail')}>
              <input
                value={form.email}
                onChange={(event) => onChange({ ...form, email: event.target.value })}
                aria-label={tt('E-mail')}
                className={INPUT}
              />
            </Field>
            <Field label={tt('Telefón')}>
              <PhoneInput
                value={form.phone}
                onChange={(next) => onChange({ ...form, phone: next })}
                label={tt('Telefón')}
                testId="employee"
              />
            </Field>
          </div>

          <Field label={editingId === null ? tt('Heslo') : tt('Nové heslo (nechajte prázdne)')}>
            <input
              type="password"
              value={form.password}
              onChange={(event) => onChange({ ...form, password: event.target.value })}
              aria-label={tt('Heslo')}
              className={INPUT}
            />
          </Field>

          {terminalConfigured && (
            <Field label={tt('Číslo karty (terminál)')}>
              <input
                value={form.hikvisionCardNo}
                onChange={(event) => onChange({ ...form, hikvisionCardNo: event.target.value })}
                aria-label={tt('Číslo karty')}
                className={INPUT}
              />
            </Field>
          )}

          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.showWallet}
              onChange={(event) => onChange({ ...form, showWallet: event.target.checked })}
              aria-label={tt('Zobrazovať peňaženku')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Zobrazovať peňaženku - koľko hotovosti pri sebe nesie</T>
          </label>
          {/* §9.2 - only for a driver, because it decides nothing for anybody else: it is what makes
            * the per-delivery reward apply and what `/external-drivers` lists. Until now the column
            * had one writer, the retired driver app's self-registration, so no administrator could
            * create the external driver the whole feature is about. */}
          {form.systemRole === 'driver' && (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.isExternal}
                onChange={(event) => onChange({ ...form, isExternal: event.target.checked })}
                aria-label={tt('Externý vodič')}
                data-employee-external
                className="w-4 h-4 rounded accent-primary"
              />
              <T>Externý vodič - platený za rozvoz, nie hodinovo</T>
            </label>
          )}
          {/* §9.685, owner #25 - *Hlavný admin by nemohol vedieť aj chodiť na rozvoz, že nemusí mať druhý účet?* Any
            * role but a driver, who drives by role and for whom the switch would say nothing. */}
          {form.systemRole !== 'driver' && (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={form.alsoDrives}
                onChange={(event) => onChange({ ...form, alsoDrives: event.target.checked })}
                aria-label={tt('Jazdí aj rozvoz')}
                data-employee-also-drives
                className="w-4 h-4 rounded accent-primary"
              />
              {/* §9.701, owner O85 - it said *ponúkne sa pri vyrazení rozvozu*, unconditionally, and the operator ticked it and
                * looked for the name on the board. The board offers whoever is on shift and was seen in four hours, a
                * marked account exactly as a driver, and the shift is started on the driving surface. */}
              <T>Jazdí aj rozvoz - na nástenke rozvozu sa ponúkne, keď si v Rozvoze zapne smenu, a jeho rozvozy sa mu rátajú do peňaženky</T>
            </label>
          )}
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(event) => onChange({ ...form, active: event.target.checked })}
              aria-label={tt('Aktívny')}
              className="w-4 h-4 rounded accent-primary"
            />
            <T>Aktívny - môže sa prihlásiť</T>
          </label>

          {locksOut && (
            <p className="flex items-start gap-1.5 text-xs text-red-500" data-locks-out>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              {/* Nothing in legacy stops a main administrator demoting themselves. */}
              <T>
                Toto je posledný hlavný administrátor. Po zmene roly sa do nastavení nikto nedostane.
              </T>
            </p>
          )}

          {problems.length > 0 && (
            <div className="space-y-1" data-employee-problems>
              {problems.map((problem) => (
                <p
                  key={problem.kind}
                  className={cn(
                    'flex items-start gap-1.5 text-xs',
                    problem.kind === 'position-duplicate' || problem.kind === 'no-credentials'
                      ? 'text-amber-500'
                      : 'text-red-500',
                  )}
                >
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                  <ProblemText problem={problem} />
                </p>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            <T>Zrušiť</T>
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !canSaveEmployee(problems) || locksOut}
            className={cn('flex-1 px-4 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            data-employee-save
          >
            {saving ? <Spinner className="w-4 h-4 mx-auto" /> : <T>Uložiť</T>}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProblemText({ problem }: { problem: EmployeeProblem }) {
  if (problem.kind === 'no-name') return <T>Meno je povinné.</T>
  if (problem.kind === 'name-too-short') return <T>Meno má mať aspoň dva znaky.</T>
  if (problem.kind === 'no-position') return <T>Pozícia je povinná.</T>
  if (problem.kind === 'position-is-role') {
    return (
      <span>
        <strong>{problem.value}</strong>{' '}
        <T>je systémová rola, nie pozícia. Pozícia je to, čo človek robí.</T>
      </span>
    )
  }
  if (problem.kind === 'position-duplicate') {
    return (
      <span>
        <T>Táto pozícia sa už používa ako</T> <strong>{problem.existing}</strong> -{' '}
        <T>dve písania toho istého sa v prehľadoch počítajú zvlášť.</T>
      </span>
    )
  }
  if (problem.kind === 'bad-email') return <T>E-mail nemá platný tvar.</T>
  if (problem.kind === 'email-taken') {
    return (
      <span>
        <T>Tento e-mail už má</T> <strong>{problem.name}</strong>.
      </span>
    )
  }
  if (problem.kind === 'password-too-short') return <T>Heslo má mať aspoň šesť znakov.</T>
  return <T>Bez e-mailu a bez hesla sa tento človek nebude môcť prihlásiť.</T>
}

// ─── small pieces ────────────────────────────────────────────────────────────

const INPUT =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/20 transition-all'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs text-muted-foreground uppercase tracking-wide font-medium block">
        {label}
      </span>
      {children}
    </div>
  )
}

/** A person's role in the *Rola* column - O119, §9.753: a custom role says what it works as when hovered. */
function RoleBadge({ role }: { role: RosterRole }) {
  return <StatusBadge label={role.label} color={role.color} title={role.worksAs ?? undefined} />
}

/** A custom role's card in the legend: its badge, what it works as, and the description written for it. */
function CustomRoleLegend({ role }: { role: RoleView }) {
  const description = role.description?.trim() ?? ''
  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 space-y-1.5"
      data-role-legend-custom={role.key}
    >
      <StatusBadge
        label={roleDisplayName(role)}
        color={roleBadgeColor(role.color, roleColor(role.baseRole))}
      />
      <p className="text-[11px] text-muted-foreground">
        · {tt('Pracuje ako')}: {roleLabel(role.baseRole)}
      </p>
      {description !== '' && <p className="text-[11px] text-muted-foreground">· {description}</p>}
    </div>
  )
}

/** Disabled with the reason in its title, rather than a toast after the click. */

/**
 * The server's own sentence, or null - §9.365, FB-652.
 *
 * **This read `record.error ?? record.message` off the thrown value.** `ApiError` has no `error`
 * property - the envelope is in `data` - and its `message` is the **developer** string built by
 * `buildDeveloperMessage`, so every refusal reached the operator as *"HTTP 400 Bad Request: <the
 * real sentence>"*. `apiErrorMessage`'s own javadoc is the rule it broke: the backend's Slovak
 * message *"with no HTTP status glued to the front and no rewording"*.
 *
 * Thirteen files carried this helper. It delegates now rather than being replaced at each call site,
 * so `?? fallback` still reads the same everywhere and there is one reader instead of thirteen.
 */
function messageOf(error: unknown): string | null {
  return apiErrorField(error, 'error')
}

export type { Restriction }
